import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { DogIcon as Dog, ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ShoppingCartIcon as ShoppingCart, StorefrontIcon as Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError, formatDate } from "@/lib/utils-app";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StripeTaxStatusBanner from "@/components/StripeTaxStatusBanner";
import ConsentResolutionPanel from "@/components/ConsentResolutionPanel";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_STYLES = {
  pending_payment: { label: "Awaiting payment", cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  held: { label: "In escrow", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  released: { label: "Released", cls: "bg-green-50 text-green-800 border-green-200" },
  refunded: { label: "Refunded", cls: "bg-zinc-50 text-zinc-800 border-zinc-200" },
  disputed: { label: "Disputed", cls: "bg-red-50 text-red-800 border-red-200" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { label: status, cls: "bg-zinc-50 text-zinc-700 border-zinc-200" };
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-[0.15em] rounded-full border px-2 py-0.5 ${s.cls}`}
      data-testid={`escrow-status-${status}`}>
      {s.label}
    </span>
  );
}

export default function MyEscrow() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/escrow/my");
      setPurchases(r.data.purchases || []);
      setSales(r.data.sales || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const release = async (id) => {
    try {
      await api.post(`/escrow/${id}/release`);
      toast.success("Funds released to the breeder.");
      load();
    } catch (err) {
      // 2FA-enabled users get a 401 — prompt and retry once.
      const status = err?.response?.status;
      const detail = (err?.response?.data?.detail || "").toLowerCase();
      if (status === 401 && detail.includes("2fa")) {
        const code = window.prompt(
          "2FA is enabled on your account. Enter your authenticator code (or a recovery code) to release these funds:"
        );
        if (!code) return;
        try {
          await api.post(`/escrow/${id}/release`, { totp_code: code.trim() });
          toast.success("Funds released to the breeder.");
          load();
        } catch (err2) {
          toast.error(formatApiError(err2));
        }
        return;
      }
      toast.error(formatApiError(err));
    }
  };

  const dispute = async (id, reason) => {
    try {
      await api.post(`/escrow/${id}/dispute`, { reason });
      toast.success("Dispute raised — admin will review.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Marketplace</p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Sales & Purchases</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Every dog you bought or sold through the registry's escrow checkout. Releasing held funds confirms pickup and pays out the breeder.
        </p>
      </div>

      {loading ? (
        <div className="text-[#6B6B6B]">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Purchases */}
          <section data-testid="escrow-purchases-section">
            <h2 className="font-spectral text-2xl tracking-tight mb-4 flex items-center gap-2">
              <ShoppingCart size={22} weight="duotone" /> Your purchases
              <span className="text-base text-[#6B6B6B] font-manrope ml-1">({purchases.length})</span>
            </h2>
            {purchases.length === 0 ? (
              <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-8 text-center text-[#6B6B6B]">
                No purchases yet. Browse the <Link to="/marketplace" className="font-semibold text-ink hover:text-crimson">marketplace</Link>.
              </div>
            ) : (
              <div className="space-y-3">
                {purchases.map((p) => (
                  <EscrowRow
                    key={p.id}
                    row={p}
                    side="buyer"
                    onRelease={release}
                    onDispute={dispute}
                    onConsentResolved={load}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            )}
          </section>
          {/* Sales */}
          <section data-testid="escrow-sales-section">
            <StripeTaxStatusBanner persona="breeder" />
            <h2 className="font-spectral text-2xl tracking-tight mb-4 flex items-center gap-2">
              <Storefront size={22} weight="duotone" /> Your sales
              <span className="text-base text-[#6B6B6B] font-manrope ml-1">({sales.length})</span>
            </h2>
            {sales.length === 0 ? (
              <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-8 text-center text-[#6B6B6B]">
                No sales yet. List one of <Link to="/dogs" className="font-semibold text-ink hover:text-crimson">your dogs</Link> on the marketplace.
              </div>
            ) : (
              <div className="space-y-3">
                {sales.map((s) => (
                  <EscrowRow
                    key={s.id}
                    row={s}
                    side="seller"
                    onConsentResolved={load}
                    currentUserId={user?.id}
                  />
                ))}
              </div>
            )}
            {/* Tax-setup nudge for breeder sellers — pointing them at the
                step-by-step Stripe Tax instructions whether they have
                sales yet or not, since Tax setup is a once-only task
                that's best done BEFORE the first sale. */}
            <div
              className="mt-5 bg-cream/40 border border-[#E2DAC2] rounded-xl p-4 flex items-start gap-3"
              data-testid="escrow-tax-setup-nudge"
            >
              <span className="text-[18px] flex-shrink-0">📑</span>
              <div className="flex-1 min-w-0 text-[13px] text-[#3F3F3F] leading-relaxed">
                <strong className="text-ink">California sales tax on dog sales?</strong>{" "}
                Stripe Tax can calculate and collect it for you automatically — funds flow
                straight to your Stripe account for filing.{" "}
                <Link
                  to="/help/stripe-tax-setup"
                  className="font-semibold text-ink underline hover:text-crimson"
                  data-testid="escrow-tax-setup-link"
                >
                  See the 5-step setup →
                </Link>
              </div>
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}

function EscrowRow({ row, side, onRelease, onDispute, onConsentResolved, currentUserId }) {
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid={`escrow-row-${row.id}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <Link
          to={`/dogs/${row.dog_id}`}
          className="flex items-center gap-2 min-w-0 hover:text-crimson"
        >
          <Dog size={18} weight="duotone" className="flex-shrink-0" />
          <span className="font-spectral text-lg truncate">{row.dog_name || "Dog"}</span>
        </Link>
        <StatusPill status={row.status} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm text-[#6B6B6B] mb-3">
        <div><span className="block text-xs uppercase tracking-wider">Amount</span><span className="text-ink font-medium">${row.amount?.toFixed(2)}</span></div>
        <div><span className="block text-xs uppercase tracking-wider">{side === "buyer" ? "Seller" : "Buyer"}</span>
          <span className="text-ink font-medium truncate block">{side === "buyer" ? "Breeder" : (row.buyer_name || row.buyer_email || "—")}</span>
        </div>
        <div><span className="block text-xs uppercase tracking-wider">Started</span><span className="text-ink">{formatDate(row.created_at)}</span></div>
        {row.status === "held" && (
          <div><span className="block text-xs uppercase tracking-wider">Auto-release</span><span className="text-ink">{formatDate(row.auto_release_at)}</span></div>
        )}
      </div>
      {row.dispute_reason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900 mb-3">
          <strong>Dispute reason:</strong> {row.dispute_reason}
        </div>
      )}
      {/* Buyer's refund-policy acknowledgement receipt — proves they
          ticked the "no refunds / no mediation" click-wrap at escrow
          purchase. Only renders to the buyer, only when the version
          stamp exists on the row. */}
      {side === "buyer" && row.refund_policy_version && (
        <p
          className="text-[10.5px] text-[#0F8043] mb-3 -mt-1 inline-flex items-center gap-1 font-semibold"
          data-testid={`escrow-refund-ack-pill-${row.id}`}
          title={`Acknowledged v${row.refund_policy_version}`}
        >
          <CheckCircle size={11} weight="fill" />
          Refund policy acknowledged · v{row.refund_policy_version}
          <a
            href="/refund#service-provider-bookings"
            target="_blank"
            rel="noreferrer"
            className="underline ml-1 hover:text-ink"
          >
            view
          </a>
        </p>
      )}
      {row.status === "disputed" && (
        <ConsentResolutionPanel
          proposeUrl={`/escrow/${row.id}/consent/propose`}
          acceptUrl={`/escrow/${row.id}/consent/accept`}
          proposals={row.consent_proposals || []}
          currentUserId={currentUserId}
          myRoleLabel={side === "buyer" ? "buyer" : "seller"}
          counterpartyLabel={side === "buyer" ? "the breeder" : "the buyer"}
          disputeFiledAt={row.disputed_at}
          onResolved={onConsentResolved}
          testIdPrefix={`escrow-consent-${row.id}`}
        />
      )}
      {side === "buyer" && row.status === "held" && (
        <div className="flex flex-wrap gap-2 pt-3 border-t border-[#E5E5E5]">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" data-testid={`escrow-confirm-pickup-${row.id}`}>
                <CheckCircle size={14} className="mr-1.5" weight="fill" /> Confirm pickup & release
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Release ${row.amount?.toFixed(2)} to the breeder?</AlertDialogTitle>
                <AlertDialogDescription>
                  Only confirm after you've received the dog and inspected it. This action is final — funds cannot be recalled.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onRelease(row.id)} data-testid={`escrow-confirm-pickup-action-${row.id}`}>
                  Yes, release funds
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50" data-testid={`escrow-dispute-${row.id}`}>
                <Warning size={14} className="mr-1.5" /> Raise dispute
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Raise a dispute</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-[#6B6B6B] mb-2">
                Tell us what's wrong. Filing a dispute freezes the funds.
                Resolution is then between you and the breeder: either you
                both agree on an outcome (full refund, full release, or any
                split), or after 30 days the platform mechanically refunds
                you in full. <a href="/refund-policy#escrow" target="_blank" rel="noreferrer" className="underline">See the published refund policy</a>.
              </p>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="What happened?"
                rows={5}
                data-testid={`escrow-dispute-reason-${row.id}`}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  disabled={!reason.trim()}
                  onClick={() => { onDispute(row.id, reason.trim()); setOpen(false); }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid={`escrow-dispute-submit-${row.id}`}
                >
                  Submit dispute
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {side === "buyer" && row.status === "released" && (row.seller_breeder_slug || row.seller_breeder_id) && (
        <div className="pt-3 border-t border-[#E5E5E5] space-y-2">
          <Link
            to={`/breeders/${row.seller_breeder_slug || row.seller_breeder_id}#reviews`}
            data-testid={`escrow-leave-review-${row.id}`}
            className="inline-flex items-center gap-2 text-[12px] font-bold text-ink hover:text-crimson"
          >
            <CheckCircle size={14} weight="fill" className="text-[#3DA56E]" />
            Leave a verified-purchase review for {row.seller_kennel_name || "this breeder"} →
          </Link>
          {/* Cross-persona handoff for the buyer's new dog. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-[#6B6B6B] pt-1">
            <Link
              to="/find-pros"
              data-testid={`escrow-find-pros-${row.id}`}
              className="hover:text-ink hover:underline"
            >
              Find a trainer or groomer →
            </Link>
            <Link
              to="/storefronts"
              data-testid={`escrow-shop-storefronts-${row.id}`}
              className="hover:text-ink hover:underline"
            >
              Shop kennel storefronts →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
